import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ProjectData, RabEntry } from '../../types';
import { fitLogoBox, imgFmt } from './letterhead';
import { groupEntriesByCategory } from '../../components/UfsA/ufsaOrdering';
import { unterlagePdfDateiname } from '../../components/UfsA/ufsaAblage';

// ═══════════════════════════════════════════════════════════════
// UNTERLAGE FÜR SPÄTERE ARBEITEN — PDF GENERATOR (jsPDF native)
// Replaces html2canvas screenshot approach with pixel-perfect PDF
// ═══════════════════════════════════════════════════════════════

const MG_L = 20; // left margin
const MG_R = 20;
const MG_T = 20;
const MG_B = 25;

// Fortsetzungskopf: Beschriftung, 7 mm darunter die Trennlinie, 6 mm darunter
// beginnt der Inhalt. Aus denselben Zahlen ergibt sich, ab wo eine Folgeseite
// beschrieben werden darf — autoTable braucht das als margin.top, sonst malt es
// seine Zeilen ÜBER den Kopf (sein Standard-Oberrand: 40 pt = 14,1 mm).
const CONT_RULE_OFF = 7;
const CONT_RULE_GAP = 6;
const CONT_HEAD_Y = MG_T + 10;
const CONT_BODY_Y = CONT_HEAD_Y + CONT_RULE_OFF + CONT_RULE_GAP;

const COLORS = {
    black: [15, 23, 42] as [number, number, number],
    dark: [30, 41, 59] as [number, number, number],
    text: [51, 65, 85] as [number, number, number],
    muted: [100, 116, 139] as [number, number, number],
    light: [148, 163, 184] as [number, number, number],
    veryLight: [226, 232, 240] as [number, number, number],
    bg: [248, 250, 252] as [number, number, number],
    white: [255, 255, 255] as [number, number, number],
    catBg: [241, 245, 249] as [number, number, number],
};

const hexToRgb = (hex: string): [number, number, number] => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
};

const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
        const parts = dateStr.split('-');
        if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
        return dateStr;
    } catch { return dateStr; }
};

const wrapTextWithGermanHyphenation = (
    text: string, 
    colWidth: number, 
    fontSize: number, 
    doc: jsPDF, 
    fontStyle: 'normal' | 'bold' | 'italic' = 'normal'
): string => {
    if (!text) return '';
    
    // Usable width in mm (column width minus cell padding and a safety buffer)
    const usableW = colWidth - 4.2;

    // Set doc font properties before measuring anything
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', fontStyle);

    // Hyphenation dictionary using '|' as syllable breaks
    const HYPHENATION_DICT: Record<string, string> = {
        'dachentwässerung': 'Dach|ent|wäs|se|rung',
        'hauptverteiler': 'Haupt|ver|tei|ler',
        'unterverteiler': 'Un|ter|ver|tei|ler',
        'druckerhöhung': 'Druck|er|hö|hung',
        'personenaufzug': 'Per|so|nen|auf|zug',
        'brandschutztür': 'Brand|schutz|tür',
        'bodenbeläge': 'Bo|den|be|lä|ge',
        'verkehrswege': 'Ver|kehrs|we|ge',
        'auffangwannen': 'Auf|fang|wan|nen',
        'gefahrstoffschrank': 'Ge|fahr|stoff|schrank',
        'sicherheitsbeleuchtung': 'Si|cher|heits|be|leuch|tung',
        'fluchtwege': 'Flucht|we|ge',
        'dachbegrünung': 'Dach|be|grü|nung',
        'befahrungsanlage': 'Be|fah|rungs|an|la|ge',
        'blitzschutz': 'Blitz|schutz',
        'blitzschutzanlage': 'Blitz|schutz|an|la|ge',
        'entwässerung': 'Ent|wäs|se|rung',
        'gefährdungsbeurteilung': 'Ge|fähr|dungs|be|ur|tei|lung',
        'instandhaltungs': 'In|stand|hal|tungs',
        'lüftungsanlage': 'Lüf|tungs|an|la|ge',
        'rauchabzugs': 'Rauch|ab|zugs',
        'wärmeabzugs': 'Wär|me|ab|zugs',
        'trinkwasser': 'Trink|was|ser',
        'entnahmestellen': 'Ent|nah|me|stel|len',
        'notstromaggregat': 'Not|strom|ag|gre|gat',
        'verbrennungsmotor': 'Ver|bren|nungs|mo|tor',
        'kraftstoffsystem': 'Kraft|stoff|sys|tem',
        'lärmbelastung': 'Lärm|be|las|tung',
        'hebeanlage': 'He|be|an|la|ge',
        'pumpensumpf': 'Pum|pen|sumpf',
        'haupteingang': 'Haupt|ein|gang',
        'schneefang': 'Schnee|fang',
        'schneefanghalterungen': 'Schnee|fang|hal|te|run|gen',
        'verankerung': 'Ver|an|ke|rung',
        'gastherme': 'Gas|ther|me',
        'schallschutz': 'Schall|schutz',
        'absturzsicherung': 'Ab|sturz|si|che|rung',
        'arbeitssicherheit': 'Ar|beits|si|cher|heit',
        'vorschriften': 'Vor|schrif|ten',
        'instandsetzung': 'In|stand|set|zung',
        'sicherheitsgeschirr': 'Si|cher|heits|ge|schirr',
        'reinigungskraft': 'Rei|ni|gungs|kraft',
        'reinigungsarbeiten': 'Rei|ni|gungs|ar|bei|ten',
        'prüfungsintervall': 'Prü|fungs|in|ter|vall',
        'bedienungsanleitung': 'Be|die|nungs|an|lei|tung',
        'durchsturzsicherung': 'Durch|sturz|si|che|rung',
        'lamellenfenster': 'La|mel|len|fens|ter',
        'fettabscheider': 'Fett|ab|schei|der',
        'wartungsvertrag': 'War|tungs|ver|trag',
        'brandschutzklappen': 'Brand|schutz|klap|pen',
        'klimatruhe': 'Kli|ma|tru|he',
        'hauptniederspannungsverteiler': 'Haupt|nie|der|span|nungs|ver|tei|ler',
        'wärmeabzugsanlage': 'Wär|me|ab|zugs|an|la|ge',
        'fluchtwegbeleuchtung': 'Flucht|weg|be|leuch|tung',
        'handfeuerlöscher': 'Hand|feu|er|lö|scher',
        'rauchschutztüren': 'Rauch|schutz|tü|ren',
        'feststellanlagen': 'Fest|stell|an|la|gen',
        'wandhydranten': 'Wand|hy|dran|ten',
        'rettungswegetüren': 'Ret|tungs|we|ge|tü|ren',
        'panikschlösser': 'Pa|nik|schlös|ser',
        'zirkulationssystem': 'Zir|ku|la|ti|ons|sys|tem',
        'legionellenschutz': 'Le|gi|o|nel|len|schutz',
        'abwasserhebeanlage': 'Ab|was|ser|he|be|an|la|ge',
        'fäkalienhebeanlage': 'Fä|ka|li|en|he|be|an|la|ge',
        'fäkalienpumpe': 'Fä|ka|li|en|pum|pe',
        'steildachbegehung': 'Steil|dach|be|ge|hung',
        'auffälligkeiten': 'Auf|fäl|lig|kei|ten'
    };

    // Helper to split common German compound word endings
    const splitCompoundWordCustom = (word: string): string[] => {
        const lower = word.toLowerCase();
        const suffixes = ['schutz', 'leiter', 'begehung', 'anlage', 'öffnung', 'gitter', 'beurteilung', 'ordnung', 'sicherung', 'stelle', 'einrichtung', 'prüfung', 'träger', 'gruppe', 'verteiler', 'schrank', 'bereich', 'aufzug', 'klappe', 'tür'];
        for (const s of suffixes) {
            if (lower.endsWith(s) && word.length > s.length + 3) {
                const idx = lower.lastIndexOf(s);
                return [word.slice(0, idx), word.slice(idx)];
            }
        }
        return [];
    };

    const hyphenateWordOnSyllable = (word: string): string[] => {
        const cleaned = word.replace(/[^a-zA-ZäöüÄÖÜß-]/g, '').toLowerCase();
        const dictEntry = HYPHENATION_DICT[cleaned];
        
        let syllables: string[] = [];
        if (dictEntry) {
            syllables = dictEntry.split('|');
            let charIdx = 0;
            syllables = syllables.map(s => {
                const orig = word.substr(charIdx, s.length);
                charIdx += s.length;
                return orig;
            });
        } else {
            const compound = splitCompoundWordCustom(word);
            if (compound.length > 1) {
                return compound;
            }

            if (word.length > 10) {
                const mid = Math.floor(word.length / 2);
                syllables = [word.slice(0, mid), word.slice(mid)];
            } else {
                return [word];
            }
        }
        return syllables;
    };

    // Replace slash and backshash spacing to layout cleanly
    const preparedText = text.replace(/ \/ /g, ' / ').replace(/\//g, '/ ');

    const paragraphs = preparedText.split('\n');
    const finalParagraphs: string[] = [];

    for (const paragraph of paragraphs) {
        const tokens = paragraph.split(' ');
        let currentLine = '';
        const lines: string[] = [];

        for (let t = 0; t < tokens.length; t++) {
            const token = tokens[t];
            if (!token) continue;

            const testLine = currentLine ? currentLine + ' ' + token : token;
            const testW = doc.getTextWidth(testLine);

            if (testW <= usableW) {
                currentLine = testLine;
            } else {
                if (currentLine) {
                    lines.push(currentLine);
                    currentLine = '';
                }

                // Try to fit the token on a new line, splitting if necessary
                let remainingToken = token;
                
                while (doc.getTextWidth(remainingToken) > usableW) {
                    const syllables = hyphenateWordOnSyllable(remainingToken);
                    let prefix = '';
                    
                    if (syllables.length > 1) {
                        let temp = '';
                        let splitIndex = -1;
                        for (let s = 0; s < syllables.length; s++) {
                            const testPart = temp + syllables[s];
                            const testPartW = doc.getTextWidth(testPart + '-');
                            if (testPartW <= usableW) {
                                temp = testPart;
                                splitIndex = s;
                            } else {
                                break;
                            }
                        }
                        if (splitIndex !== -1) {
                            prefix = temp + '-';
                            remainingToken = syllables.slice(splitIndex + 1).join('');
                        }
                    }

                    if (!prefix) {
                        let temp = '';
                        for (let c = 0; c < remainingToken.length; c++) {
                            const charW = doc.getTextWidth(temp + remainingToken[c] + '-');
                            if (charW <= usableW) {
                                temp += remainingToken[c];
                            } else {
                                break;
                            }
                        }
                        if (temp) {
                            prefix = temp + '-';
                            remainingToken = remainingToken.slice(temp.length);
                        } else {
                            prefix = remainingToken[0] + '-';
                            remainingToken = remainingToken.slice(1);
                        }
                    }

                    lines.push(prefix);
                }
                
                currentLine = remainingToken;
            }
        }

        if (currentLine) {
            lines.push(currentLine);
        }

        finalParagraphs.push(lines.join('\n'));
    }

    return finalParagraphs.join('\n');
};

interface GeneratorOptions {
    showRegulations?: boolean;
    showTOC?: boolean;
    orientation?: 'portrait' | 'landscape';
    coverImageSize?: 'small' | 'medium' | 'large';
    /**
     * 'save' (Standard) lädt die Datei herunter, 'doc' liefert nur das
     * jsPDF-Dokument — für die Vorschau im Modul, die das ECHTE PDF zeigt
     * statt einer HTML-Nachbildung.
     */
    output?: 'save' | 'doc';
}

// ── MAIN EXPORT ──
export const generateUnterlagePdf = async (
    projectData: ProjectData,
    entries: RabEntry[],
    options: GeneratorOptions = {}
): Promise<jsPDF> => {
    const { showRegulations = true, orientation = 'portrait', coverImageSize = 'medium', output = 'save' } = options;
    const isLandscape = orientation === 'landscape';
    // Dynamische Seitenmaße: überschatten die Portrait-Modulkonstanten, damit alle
    // Helfer unten automatisch mit der aktiven Ausrichtung rechnen.
    const A4_W = isLandscape ? 297 : 210;
    const A4_H = isLandscape ? 210 : 297;
    const CONTENT_W = A4_W - MG_L - MG_R;
    const themeRgb = hexToRgb(projectData.themeColor || '#2563eb');
    const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4', compress: true });
    const isDraft = projectData.isDraft;
    const contacts = projectData.contacts || [];

    // ── HELPERS ──
    const addPage = () => { doc.addPage(); };

    const drawWatermark = () => {
        if (!isDraft) return;
        doc.saveGraphicsState();
        // @ts-expect-error AcroForm/GState members come from jsPDF plugins and are absent from its type definitions
        doc.setGState(new doc.GState({ opacity: 0.08 }));
        doc.setFontSize(72);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.dark);
        // Rotate text
        const cx = A4_W / 2, cy = A4_H / 2;
        doc.text('ENTWURF', cx, cy, { align: 'center', angle: 45 });
        doc.restoreGraphicsState();
    };

    const drawFooter = (pageNumber: number, totalPages: number) => {
        const y = A4_H - 12;
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.light);
        doc.text(projectData.companyName || '', MG_L, y);
        doc.setFont('helvetica', 'bold');
        doc.text(`Seite ${pageNumber} / ${totalPages}`, A4_W - MG_R, y, { align: 'right' });
    };

    const drawThemeBar = () => {
        doc.setFillColor(...themeRgb);
        doc.rect(0, 0, 4, A4_H, 'F');
    };

    const drawSectionHeader = (title: string, subtitle: string, y: number): number => {
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text(title, MG_L, y);
        y += 4;
        doc.setFillColor(...themeRgb);
        doc.rect(MG_L, y, 8, 0.5, 'F');
        if (subtitle) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...COLORS.muted);
            doc.text(subtitle.toUpperCase(), MG_L + 11, y + 1);
        }
        y += 4;
        doc.setDrawColor(...COLORS.veryLight);
        doc.line(MG_L, y, A4_W - MG_R, y);
        return y + 8;
    };

    const drawContinuationHeader = (label: string, y: number): number => {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.light);
        doc.text(label.toUpperCase(), MG_L, y);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text(projectData.projectName || '', A4_W - MG_R, y, { align: 'right' });
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.muted);
        doc.text(formatDate(projectData.date), A4_W - MG_R, y + 4, { align: 'right' });
        y += CONT_RULE_OFF;
        doc.setDrawColor(...COLORS.veryLight);
        doc.line(MG_L, y, A4_W - MG_R, y);
        return y + CONT_RULE_GAP;
    };

    // Wrap text manually, returns lines array. Schriftschnitt MUSS vor der
    // Messung gesetzt sein — bold misst breiter als normal.
    const wrapText = (text: string, maxWidth: number, fontSize: number, style: 'normal' | 'bold' | 'italic' = 'normal'): string[] => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', style);
        return doc.splitTextToSize(text, maxWidth);
    };

    // Gemessene Zeilenhöhe in mm für eine Punktgröße (jsPDF-Standard-Lineheight 1.15,
    // 1pt = 1/72 inch = 0.3528 mm). Ersetzt die fehleranfälligen Magic-Number-Höhen.
    const lineH = (fontSize: number): number => fontSize * 1.15 * 0.352778;

    // Spaltenbreiten auf die aktive Inhaltsbreite skalieren. Die Summe MUSS
    // EXAKT CONTENT_W sein: Spalten mit fester Breite kann autoTable nicht
    // nachziehen und warnt sonst bei jedem Lauf („Of the table content, N units
    // width could not fit page"). Der Rundungsrest landet auf der letzten
    // Spalte, damit die Summe auch im Querformat aufgeht.
    const scaleCols = (base: number[]): number[] => {
        const sum = base.reduce((a, b) => a + b, 0);
        const scaled = base.map(w => (w / sum) * CONTENT_W);
        scaled[scaled.length - 1] = CONTENT_W - scaled.slice(0, -1).reduce((a, b) => a + b, 0);
        return scaled;
    };

    // Wie wrapText, aber MIT deutscher Silbentrennung (bricht auch lange Komposita/Wörter).
    // Liefert ein Zeilen-Array. usableW im Hyphenator = maxWidth (er zieht intern 4.2 ab).
    const wrapHyph = (
        text: string,
        maxWidth: number,
        fontSize: number,
        style: 'normal' | 'bold' | 'italic' = 'normal'
    ): string[] => {
        const wrapped = wrapTextWithGermanHyphenation(text || '', maxWidth + 4.2, fontSize, doc, style);
        return wrapped ? wrapped.split('\n') : [];
    };

    // E-Mail-Adressen (und Ähnliches) brechen nur an vorhandenen Trennzeichen
    // (. @ - _). Umbruch VOR dem Zeichen: die Folgezeile beginnt mit -/./@,
    // damit ein Bindestrich am Zeilenende nie wie ein Trennstrich aussieht
    // (Duden-Konvention für URL-/Adressumbrüche). Es wird nichts eingefügt.
    const wrapEmailLike = (text: string, maxWidth: number, fontSize: number): string[] => {
        if (!text) return [];
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', 'normal');
        const segments = text.match(/[.@\-_][^.@\-_]*|[^.@\-_]+/g) || [text];
        const lines: string[] = [];
        let line = '';
        for (const seg of segments) {
            if (line && doc.getTextWidth(line + seg) > maxWidth) {
                lines.push(line);
                line = '';
            }
            if (!line && doc.getTextWidth(seg) > maxWidth) {
                // Einzelsegment breiter als die Spalte (praktisch selten):
                // zeichenweiser Fallback ohne eingefügte Zeichen.
                const parts = doc.splitTextToSize(seg, maxWidth) as string[];
                lines.push(...parts.slice(0, -1));
                line = parts[parts.length - 1] ?? '';
            } else {
                line += seg;
            }
        }
        if (line) lines.push(line);
        return lines;
    };

    // Zeichnet ein Bild seitenverhältnis-treu (object-contain) in eine Box,
    // statt es auf die Boxmaße zu verzerren.
    const drawImageFitted = (imageData: string, boxX: number, boxY: number, boxW: number, boxH: number) => {
        const props = doc.getImageProperties(imageData);
        const imgRatio = props.width / props.height;
        const boxRatio = boxW / boxH;
        let drawW = boxW, drawH = boxH, drawX = boxX, drawY = boxY;
        if (imgRatio > boxRatio) {
            drawH = boxW / imgRatio;
            drawY = boxY + (boxH - drawH) / 2;
        } else {
            drawW = boxH * imgRatio;
            drawX = boxX + (boxW - drawW) / 2;
        }
        doc.addImage(imageData, 'AUTO', drawX + 0.5, drawY + 0.5, drawW - 1, drawH - 1);
    };

    // ════════════════════════════════════════════
    // PAGE 1: DECKBLATT (COVER)
    // ════════════════════════════════════════════
    const renderCover = async () => {
        if (isLandscape) { await renderCoverLandscape(); return; }
        drawThemeBar();
        drawWatermark();

        // RAB 32 header
        let y = 35;
        doc.setFillColor(...themeRgb);
        doc.rect(MG_L, y - 5, 0.8, 25, 'F'); // left accent bar

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.muted);
        doc.text('RAB 32', MG_L + 5, y);

        doc.setFontSize(36);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text('UNTERLAGE', MG_L + 5, y + 13);

        doc.setFontSize(14);
        doc.setFont('times', 'italic');
        doc.setTextColor(...COLORS.light);
        doc.text('für spätere Arbeiten an baulichen Anlagen', MG_L + 5, y + 21);

        // Company logo (top right)
        if (projectData.companyLogo) {
            try {
                // Starre 40x14-Box verzerrte jedes Logo, dessen Seitenverhaeltnis
                // nicht zufaellig passte. fitLogoBox passt es in dieselbe Box ein;
                // Anker bleibt die rechte untere Ecke der bisherigen Box.
                const box = fitLogoBox(doc.getImageProperties(projectData.companyLogo), 40, 14);
                if (box) {
                    doc.addImage(projectData.companyLogo, imgFmt(projectData.companyLogo), A4_W - MG_R - 5 - box.width, 25 + (14 - box.height), box.width, box.height);
                }
            } catch (e) { console.warn('Logo load error', e); }
        }

        // Project image (Höhe je nach Deckblatt-Bildgröße-Einstellung)
        y = 80;
        if (projectData.projectImage) {
            try {
                const imgW = CONTENT_W;
                const imgH = coverImageSize === 'small' ? 45 : coverImageSize === 'large' ? 100 : 75;
                // Kein Rahmen — Bild wird randlos seitenverhältnis-treu eingepasst
                drawImageFitted(projectData.projectImage, MG_L, y, imgW, imgH);
                y += imgH + 8;
            } catch (e) {
                console.warn('Project image error', e);
                y += 5;
            }
        } else {
            // Ohne Projektbild: keinen Platzhalter ins PDF drucken
            y += 8;
        }

        // Project title
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...themeRgb);
        doc.text('BAUVORHABEN', MG_L, y);
        // Größerer Abstand, damit der 18pt-Titel nicht in das Label hineinragt.
        y += 6.5;
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        const titleLines = wrapHyph(projectData.projectName || 'Projekttitel', CONTENT_W, 18, 'bold');
        doc.text(titleLines, MG_L, y);
        y += titleLines.length * lineH(18) + 3;

        // Location
        if (projectData.location && projectData.location.trim().replace(/^,+|,+$/g, '')) {
            doc.setFontSize(11);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.text);
            const locLines = wrapHyph(projectData.location, CONTENT_W - 4, 11);
            doc.setFillColor(...themeRgb);
            doc.circle(MG_L + 1, y - 1.5, 0.8, 'F');
            doc.text(locLines, MG_L + 4, y);
            y += locLines.length * lineH(11) + 5;
        } else {
            y += 5;
        }

        // Footer grid
        doc.setDrawColor(...COLORS.veryLight);
        doc.line(MG_L, y - 4, A4_W - MG_R, y - 4);

        const colGridW = (CONTENT_W - 10) / 2; // (170 - 10) / 2 = 80 mm
        const leftColX = MG_L;
        const rightColX = MG_L + colGridW + 10;

        // Row 1: BAUHERR & KOORDINATOR
        const builderVal = projectData.builder || '—';
        const builderLines = wrapHyph(builderVal, colGridW, 9, 'bold');
        const subVal = projectData.builderAddress;
        const subLines = subVal ? wrapHyph(subVal, colGridW, 7) : [];

        const coordVal = projectData.coordinator || '—';
        const coordLines = wrapHyph(coordVal, colGridW, 9, 'bold');

        let curY = y;
        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.light);
        doc.text('BAUHERR', leftColX, curY);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text(builderLines, leftColX, curY + 4);
        let builderHeight = builderLines.length * lineH(9);

        if (subLines.length > 0) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.muted);
            doc.text(subLines, leftColX, curY + 4 + builderHeight + 1);
            builderHeight += subLines.length * lineH(7) + 1;
        }

        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.light);
        doc.text('KOORDINATOR', rightColX, curY);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text(coordLines, rightColX, curY + 4);
        let coordHeight = coordLines.length * lineH(9);

        const row1Height = Math.max(builderHeight, coordHeight) + 8;

        // Row 2: STAND & VERSION
        curY += row1Height;
        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.light);
        doc.text('STAND', leftColX, curY);

        const dateVal = formatDate(projectData.date);
        const dateLines = wrapText(dateVal, colGridW, 9);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text(dateLines, leftColX, curY + 4);
        let standHeight = dateLines.length * lineH(9);

        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.light);
        doc.text('VERSION', rightColX, curY);

        const verVal = projectData.version || '1.0';
        const verLines = wrapText(verVal, colGridW, 9);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text(verLines, rightColX, curY + 4);
        let verHeight = verLines.length * lineH(9);

        const row2Height = Math.max(standHeight, verHeight) + 8;

        // Erstellt durch
        curY += row2Height;
        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.light);
        doc.text('ERSTELLT DURCH', leftColX, curY);

        const companyVal = projectData.companyName || '';
        const companyLines = wrapHyph(companyVal, CONTENT_W, 9, 'bold');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text(companyLines, leftColX, curY + 4);
        let companyHeight = companyLines.length * lineH(9);

        if (projectData.companyAddress) {
            const companyAddrLines = wrapHyph(projectData.companyAddress, CONTENT_W, 7);
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.muted);
            doc.text(companyAddrLines, leftColX, curY + 4 + companyHeight + 1);
        }
    };

    // ════════════════════════════════════════════
    // DECKBLATT QUERFORMAT (Text links, Bild rechts — wie die Vorschau)
    // ════════════════════════════════════════════
    const renderCoverLandscape = async () => {
        drawThemeBar();
        drawWatermark();

        const leftX = MG_L;                    // 20
        const leftW = 130;
        const rightX = MG_L + leftW + 12;      // 162
        const rightW = A4_W - MG_R - rightX;   // 115

        // RAB 32 header (linke Spalte)
        let y = 32;
        doc.setFillColor(...themeRgb);
        doc.rect(leftX, y - 5, 0.8, 23, 'F');

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.muted);
        doc.text('RAB 32', leftX + 5, y);

        doc.setFontSize(30);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text('UNTERLAGE', leftX + 5, y + 11);

        doc.setFontSize(12);
        doc.setFont('times', 'italic');
        doc.setTextColor(...COLORS.light);
        doc.text('für spätere Arbeiten an baulichen Anlagen', leftX + 5, y + 18);

        // Project title
        y = 68;
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...themeRgb);
        doc.text('BAUVORHABEN', leftX, y);
        y += 6;
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        const titleLines = wrapHyph(projectData.projectName || 'Projekttitel', leftW, 16, 'bold');
        doc.text(titleLines, leftX, y);
        y += titleLines.length * lineH(16) + 3;

        // Location
        if (projectData.location && projectData.location.trim().replace(/^,+|,+$/g, '')) {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.text);
            const locLines = wrapHyph(projectData.location, leftW - 4, 10);
            doc.setFillColor(...themeRgb);
            doc.circle(leftX + 1, y - 1.5, 0.8, 'F');
            doc.text(locLines, leftX + 4, y);
            y += locLines.length * lineH(10) + 6;
        } else {
            y += 6;
        }

        // Footer grid (zwei Unter-Spalten innerhalb der linken Spalte)
        doc.setDrawColor(...COLORS.veryLight);
        doc.line(leftX, y - 4, leftX + leftW, y - 4);

        const colGridW = (leftW - 10) / 2;     // 60
        const colLX = leftX;
        const colRX = leftX + colGridW + 10;

        // Row 1: BAUHERR & KOORDINATOR
        const builderLines = wrapHyph(projectData.builder || '—', colGridW, 9, 'bold');
        const subLines = projectData.builderAddress ? wrapHyph(projectData.builderAddress, colGridW, 7) : [];
        const coordLines = wrapHyph(projectData.coordinator || '—', colGridW, 9, 'bold');

        let curY = y;
        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.light);
        doc.text('BAUHERR', colLX, curY);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text(builderLines, colLX, curY + 4);
        let builderHeight = builderLines.length * lineH(9);

        if (subLines.length > 0) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.muted);
            doc.text(subLines, colLX, curY + 4 + builderHeight + 1);
            builderHeight += subLines.length * lineH(7) + 1;
        }

        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.light);
        doc.text('KOORDINATOR', colRX, curY);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text(coordLines, colRX, curY + 4);
        const coordHeight = coordLines.length * lineH(9);

        curY += Math.max(builderHeight, coordHeight) + 8;

        // Row 2: STAND & VERSION
        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.light);
        doc.text('STAND', colLX, curY);
        doc.text('VERSION', colRX, curY);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text(formatDate(projectData.date), colLX, curY + 4);
        doc.text(projectData.version || '1.0', colRX, curY + 4);

        curY += lineH(9) + 8;

        // Erstellt durch
        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.light);
        doc.text('ERSTELLT DURCH', colLX, curY);

        const companyLines = wrapHyph(projectData.companyName || '', leftW, 9, 'bold');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.black);
        doc.text(companyLines, colLX, curY + 4);

        if (projectData.companyAddress) {
            const companyAddrLines = wrapHyph(projectData.companyAddress, leftW, 7);
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.muted);
            doc.text(companyAddrLines, colLX, curY + 4 + companyLines.length * lineH(9) + 1);
        }

        // Rechte Spalte: Projektbild (Größe einstellbar), vertikal zentriert
        const imgBoxW = coverImageSize === 'small' ? rightW * 0.65 : coverImageSize === 'large' ? rightW : rightW * 0.85;
        const imgBoxH = coverImageSize === 'small' ? 55 : coverImageSize === 'large' ? 110 : 80;
        const imgX = rightX + (rightW - imgBoxW) / 2;
        const imgY = 28 + (137 - imgBoxH) / 2;

        if (projectData.projectImage) {
            try {
                // Kein Rahmen, kein Platzhalter — randlos eingepasst
                drawImageFitted(projectData.projectImage, imgX, imgY, imgBoxW, imgBoxH);
            } catch (e) { console.warn('Project image error', e); }
        }

        // Company logo (unten rechts)
        if (projectData.companyLogo) {
            try {
                // Wie oben: seitenverhaeltnisrichtig in die 36x12-Box, rechts
                // unten verankert.
                const box = fitLogoBox(doc.getImageProperties(projectData.companyLogo), 36, 12);
                if (box) {
                    doc.addImage(projectData.companyLogo, imgFmt(projectData.companyLogo), A4_W - MG_R - 4 - box.width, A4_H - MG_B - 10 + (12 - box.height), box.width, box.height);
                }
            } catch (e) { console.warn('Logo load error', e); }
        }
    };

    // ════════════════════════════════════════════
    // PAGE 2: ADRESSVERZEICHNIS
    // ════════════════════════════════════════════
    const renderAddressPage = () => {
        if (contacts.length === 0) return;
        addPage();
        drawThemeBar();
        drawWatermark();

        const y = drawSectionHeader('Adressverzeichnis', 'Projektbeteiligte', CONT_HEAD_Y);

        // Portrait-Spaltenbreiten (30/46/40/54 mm) auf die aktive Inhaltsbreite skaliert
        const [wRole, wCompany, wAddress, wContact] = scaleCols([30, 46, 40, 54]);

        // Kontaktzellen laufen durch dieselben Umbruch-Pfade wie der Fließtext,
        // statt autoTable's eigenem Algorithmus: Firma/Name/Anschrift mit
        // Silbentrennung, Tel/E-Mail ohne (Adressen dürfen keine Trennstriche
        // oder eingefügte Leerzeichen bekommen).
        const contactCell = (parts: string[], hyphenate: boolean, colWidth: number): string => {
            const usable = colWidth - 6.5; // cellPadding 3 je Seite + Puffer
            return parts.filter(Boolean)
                .flatMap(p => hyphenate ? wrapHyph(p, usable, 7.5) : wrapText(p, usable, 7.5))
                .join('\n');
        };

        autoTable(doc, {
            startY: y,
            margin: { left: MG_L, right: MG_R, top: CONT_BODY_Y, bottom: MG_B + 5 },
            head: [['Funktion / Rolle', 'Firma', 'Anschrift', 'Kontakt']],
            body: contacts.map(c => [
                c.role || '',
                contactCell([c.company || '', c.name || ''], true, wCompany),
                contactCell([c.address || ''], true, wAddress),
                [
                    ...(c.phone ? wrapText('Tel: ' + c.phone, wContact - 6.5, 7.5) : []),
                    ...wrapEmailLike(c.email || '', wContact - 6.5, 7.5)
                ].join('\n')
            ]),
            // fillColor: false statt Weiß — sonst deckt jede Zeile das
            // Wasserzeichen zu. Die Zebra-Streifen bleiben (alternateRowStyles).
            styles: { fontSize: 7.5, cellPadding: 3, lineColor: COLORS.veryLight, lineWidth: 0.1, fillColor: false },
            headStyles: { fillColor: COLORS.black, textColor: COLORS.white, fontStyle: 'bold', fontSize: 7 },
            alternateRowStyles: { fillColor: COLORS.bg },
            columnStyles: {
                // Rolle ist kurz ("Bauherr", "SiGeKo") — Platz zur Kontakt-
                // Spalte verschieben, damit E-Mails möglichst selten brechen.
                0: { fontStyle: 'bold', textColor: themeRgb, cellWidth: wRole },
                1: { cellWidth: wCompany },
                2: { cellWidth: wAddress },
                3: { cellWidth: wContact }
            },
            // Folgeseiten bekommen denselben Kopf wie der Katalog; ohne
            // margin.top begänne die Liste bei 14 mm ÜBER dem Kopf.
            willDrawPage: (data: { pageNumber: number }) => {
                if (data.pageNumber > 1) {
                    drawThemeBar();
                    drawWatermark();
                    drawContinuationHeader('Adressverzeichnis (Fortsetzung)', CONT_HEAD_Y);
                }
            }
        });
    };

    // ════════════════════════════════════════════
    // PREAMBLE PAGES (Einführung & Grundsätze)
    // ════════════════════════════════════════════
    const renderPreamble = () => {
        if (!projectData.preamble) return;

        addPage();
        drawThemeBar();
        drawWatermark();

        let y = drawSectionHeader('Einführung & Grundsätze', 'Rechtliche Hinweise', MG_T + 10);
        const maxY = A4_H - MG_B - 5; // Extra 5mm buffer to avoid near-empty continuation pages
        // Im Querformat Fließtext auf 170 mm begrenzen — volle 257 mm wären unleserlich lange Zeilen
        const TEXT_W = isLandscape ? 170 : CONTENT_W;

        const lines = projectData.preamble.split('\n');
        let listBuffer: string[] = [];

        const flushList = () => {
            if (listBuffer.length === 0) return;
            
            // Pre-calculate heights for all remaining items
            const itemHeights = listBuffer.map(item => {
                const wrapped = wrapText(item, TEXT_W - 8, 8);
                return wrapped.length * 3.5 + 1.5;
            });
            const totalListHeight = itemHeights.reduce((a, b) => a + b, 0);
            
            // If everything fits on this page, render it all
            if (y + totalListHeight <= maxY) {
                listBuffer.forEach((item, i) => {
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(...COLORS.text);
                    doc.setFillColor(...COLORS.dark);
                    doc.circle(MG_L + 3, y - 1, 0.4, 'F');
                    const wrapped = wrapText(item, TEXT_W - 8, 8);
                    doc.text(wrapped, MG_L + 6, y);
                    y += itemHeights[i];
                });
            } else {
                // Render what fits, then page break for rest
                listBuffer.forEach((item, i) => {
                    if (y + itemHeights[i] > maxY) {
                        addPage();
                        drawThemeBar(); drawWatermark();
                        y = drawContinuationHeader('Rechtliche Hinweise (Fortsetzung)', MG_T + 10);
                    }
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(...COLORS.text);
                    doc.setFillColor(...COLORS.dark);
                    doc.circle(MG_L + 3, y - 1, 0.4, 'F');
                    const wrapped = wrapText(item, TEXT_W - 8, 8);
                    doc.text(wrapped, MG_L + 6, y);
                    y += itemHeights[i];
                });
            }
            
            listBuffer = [];
            y += 1;
        };

        for (const line of lines) {
            const trimmed = line.trim();

            // Manual page break
            if (trimmed.toUpperCase() === '[UMBRUCH]') {
                flushList();
                addPage();
                drawThemeBar(); drawWatermark();
                y = drawContinuationHeader('Rechtliche Hinweise (Fortsetzung)', MG_T + 10);
                continue;
            }

            if (!trimmed) {
                flushList();
                y += 2;
                continue;
            }

            const isHeader = /^\d+\.?\d*\.?\s/.test(trimmed);
            const isListItem = /^[\•\-\*]\s/.test(trimmed);

            if (isHeader) {
                flushList();
                // Erst umbrechen, dann echte Blockhöhe gegen die Seite prüfen —
                // die alte 12mm-Pauschale ließ mehrzeilige Header in den Footer laufen.
                const headerLines = wrapText(trimmed, TEXT_W, 10, 'bold');
                const headerH = 4 + headerLines.length * 4.5 + 3;
                if (y + headerH > maxY) {
                    addPage();
                    drawThemeBar(); drawWatermark();
                    y = drawContinuationHeader('Rechtliche Hinweise (Fortsetzung)', MG_T + 10);
                }
                y += 4;
                doc.setFontSize(10);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...COLORS.black);
                doc.text(headerLines, MG_L, y);
                y += headerLines.length * 4.5 + 3;
            } else if (isListItem) {
                listBuffer.push(trimmed.replace(/^[\•\-\*]\s/, ''));
            } else {
                flushList();
                const textLines = wrapText(trimmed, TEXT_W, 8);
                const blockH = textLines.length * 3.5 + 2;
                if (y + blockH > maxY) {
                    addPage();
                    drawThemeBar(); drawWatermark();
                    y = drawContinuationHeader('Rechtliche Hinweise (Fortsetzung)', MG_T + 10);
                }
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(...COLORS.text);
                doc.text(textLines, MG_L, y);
                y += textLines.length * 3.5 + 2;
            }
        }
        flushList();
    };

    // ════════════════════════════════════════════
    // CATALOG PAGES (Maßnahmenkatalog)
    // ════════════════════════════════════════════
    const renderCatalog = () => {
        if (entries.length === 0) return;

        addPage();
        drawThemeBar();
        drawWatermark();

        const y = drawSectionHeader('Maßnahmenkatalog', projectData.projectName || '', CONT_HEAD_Y);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.muted);
        doc.text('Sicherheitstechnische Einrichtungen', MG_L + (doc.getTextWidth(projectData.projectName || '') > 0 ? 4 : 0), y - 5);

        // EINE Gruppierung für Liste und PDF (src/components/UfsA/ufsaOrdering.ts).
        // Sie sortiert die Kategorien nach der eingestellten Reihenfolge und
        // nennt eine leere Kategorie „Sonstiges" (früher „Allgemein").
        const groups = groupEntriesByCategory(entries, projectData.categoryOrder);

        let isFirstCatOnPage = true;
        // finalY der zuletzt gezeichneten KATALOG-Tabelle. `doc.lastAutoTable`
        // darf hier nicht gelesen werden: vor der ersten Katalogtabelle hält es
        // noch das Adressverzeichnis der Vorseite — dessen finalY riss ein Loch
        // von rund 100 mm zwischen Überschrift und erster Kategorie.
        let prevFinalY: number | null = null;

        const scaled = showRegulations
            ? scaleCols([30, 28, 32, 54, 26])
            : scaleCols([35, 35, 40, 60]);
        const colWidths = {
            comp: scaled[0],
            act: scaled[1],
            haz: scaled[2],
            meas: scaled[3],
            reg: showRegulations ? scaled[4] : 0
        };

        const headRow = showRegulations
            ? ['BAUTEIL / ANLAGE', 'TÄTIGKEIT / TURNUS', 'GEFÄHRDUNGEN', 'MASSNAHMEN', 'REGELWERKE']
            : ['BAUTEIL / ANLAGE', 'TÄTIGKEIT / TURNUS', 'GEFÄHRDUNGEN', 'MASSNAHMEN'];

        const colStylesObj: Record<number, any> = {
            0: { cellWidth: colWidths.comp, fontStyle: 'bold', textColor: COLORS.black },
            1: { cellWidth: colWidths.act },
            2: { cellWidth: colWidths.haz },
            3: { cellWidth: colWidths.meas },
        };
        if (showRegulations) {
            colStylesObj[4] = { cellWidth: colWidths.reg, fontStyle: 'italic', textColor: COLORS.muted };
        }

        groups.forEach((group) => {
            const catEntries = group.entries;

            // Category header as a separate autoTable row
            const catHeaderBody = [[{
                content: group.category.toUpperCase(),
                colSpan: showRegulations ? 5 : 4,
                styles: {
                    fillColor: COLORS.catBg,
                    textColor: COLORS.dark,
                    fontStyle: 'bold' as const,
                    fontSize: 7,
                    cellPadding: { top: 2, bottom: 2, left: 6, right: 2 }
                }
            }]];

            // Build body rows
            const bodyRows = catEntries.map(e => {
                const actCell = e.frequency
                    ? `${e.activity || ''}\n[${e.frequency}]`
                    : (e.activity || '');
                const row = [
                    wrapTextWithGermanHyphenation(e.component || '', colWidths.comp, 7, doc, 'bold'),
                    wrapTextWithGermanHyphenation(actCell, colWidths.act, 7, doc, 'normal'),
                    wrapTextWithGermanHyphenation(e.hazards || '', colWidths.haz, 7, doc, 'normal'),
                    wrapTextWithGermanHyphenation(e.measures || '', colWidths.meas, 7, doc, 'normal')
                ];
                if (showRegulations) row.push(wrapTextWithGermanHyphenation(e.regulations || '', colWidths.reg, 7, doc, 'normal'));
                return row;
            });

            // Combine: cat header row + data rows
            const allRows = [...catHeaderBody, ...bodyRows];

            // Erste Kategorie direkt unter die Überschrift, jede weitere
            // 3 mm unter die vorige Katalogtabelle.
            let startY = prevFinalY === null ? y : prevFinalY + 3;

            // Der Kategoriekopf darf nicht allein am Seitenende stehen.
            // `rowPageBreak: 'avoid'` schützt nur EINE Zeile — der Kopf ist
            // eine eigene Zeile und blieb deshalb unten zurück, während der
            // erste Eintrag auf die Folgeseite rutschte. Deshalb hier nach
            // autoTables Formel (Zeilen × Zeilenhöhe + Padding) messen, ob
            // Kopf UND erster Eintrag noch passen; sonst selbst umbrechen.
            // Die Zellen sind schon umbrochen (\n), autoTable teilt nicht
            // weiter — die Zeilenzahl ist damit exakt.
            const catHeadH = lineH(7) + 2 + 2;
            const firstRowLines = Math.max(...bodyRows[0].map(c => c.split('\n').length));
            const firstRowH = firstRowLines * lineH(7) + 1.5 * 2;
            const needed = catHeadH + firstRowH + 0.5;
            if (startY + needed > A4_H - (MG_B + 5)) {
                addPage();
                drawThemeBar();
                drawWatermark();
                drawContinuationHeader('Maßnahmenkatalog (Fortsetzung)', CONT_HEAD_Y);
                startY = CONT_BODY_Y;
            }

            autoTable(doc, {
                startY,
                // margin.top gilt für die FOLGESEITEN: dort beginnt der Inhalt
                // unter dem Fortsetzungskopf statt bei autoTables 14 mm.
                margin: { left: MG_L, right: MG_R, top: CONT_BODY_Y, bottom: MG_B + 5 },
                head: isFirstCatOnPage ? [headRow] : undefined,
                body: allRows,
                styles: {
                    fontSize: 7,
                    cellPadding: 1.5,
                    lineColor: COLORS.veryLight,
                    lineWidth: 0.1,
                    textColor: COLORS.text,
                    overflow: 'linebreak',
                    valign: 'top',
                    // Ohne Füllung bleibt das Wasserzeichen auf den
                    // Folgeseiten sichtbar (weiße Zeilen deckten es zu).
                    fillColor: false
                },
                // Ein Eintrag wird nicht über zwei Seiten zerrissen; Zeilen, die
                // höher als eine Seite sind, teilt autoTable trotzdem.
                rowPageBreak: 'avoid',
                headStyles: {
                    fillColor: COLORS.black,
                    textColor: COLORS.white,
                    fontStyle: 'bold',
                    fontSize: 6.5,
                    cellPadding: 2
                },
                columnStyles: colStylesObj,
                showHead: isFirstCatOnPage ? 'firstPage' : false,
                // Nur willDrawPage (zeichnet VOR dem Inhalt): der frühere
                // didDrawPage-Hook malte Bar + Wasserzeichen nach dem Inhalt
                // nochmal — doppelt so dunkel und ÜBER dem Text. Seite 1
                // bekommt beides bereits direkt in renderCatalog.
                willDrawPage: (data: { pageNumber: number }) => {
                    if (data.pageNumber > 1) {
                        drawThemeBar();
                        drawWatermark();
                        drawContinuationHeader('Maßnahmenkatalog (Fortsetzung)', CONT_HEAD_Y);
                    }
                }
            });

            prevFinalY = (doc as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? null;
            isFirstCatOnPage = false;
        });
    };

    // ════════════════════════════════════════════
    // MITGELTENDE UNTERLAGEN (nach dem Maßnahmenkatalog)
    // ════════════════════════════════════════════
    const renderMitgeltendeUnterlagen = () => {
        // Zeilen ohne Namen (im Editor geleert) gehören nicht ins Dokument;
        // bleibt nichts übrig, entfällt das Kapitel ganz.
        const unterlagen = (projectData.mitgeltendeUnterlagen || []).filter(u => (u.name || '').trim());
        if (unterlagen.length === 0) return;

        addPage();
        drawThemeBar();
        drawWatermark();

        let y = drawSectionHeader('Mitgeltende Unterlagen', 'Ablage beim Betreiber', CONT_HEAD_Y);

        // Einleitungssatz in Fließtextbreite (im Querformat wie die Grundsätze begrenzt).
        const introW = isLandscape ? 170 : CONTENT_W;
        const intro = wrapText(
            'Die folgenden Unterlagen gelten ergänzend zu dieser Unterlage. Sie sind vor Beginn späterer Arbeiten am angegebenen Ablageort einzusehen; maßgeblich ist jeweils der aktuelle Stand beim Betreiber.',
            introW, 8,
        );
        doc.setTextColor(...COLORS.text);
        doc.text(intro, MG_L, y);
        y += intro.length * lineH(8) + 2;

        const [wName, wOrt, wBem] = scaleCols([62, 54, 54]);

        // Startpunkt ausdrücklich aus der Überschrift — NIE aus
        // doc.lastAutoTable (das hielte hier noch die letzte Katalogtabelle,
        // siehe prevFinalY in renderCatalog).
        autoTable(doc, {
            startY: y,
            // margin.top für die Folgeseiten: Inhalt unter dem Fortsetzungskopf.
            margin: { left: MG_L, right: MG_R, top: CONT_BODY_Y, bottom: MG_B + 5 },
            head: [['UNTERLAGE', 'ABLAGEORT', 'BEMERKUNG']],
            // Vorab umbrochen wie der Katalog (Silbentrennung) — autoTable
            // teilt die Zeilen dann nicht noch einmal anders.
            body: unterlagen.map(u => [
                wrapTextWithGermanHyphenation(u.name.trim(), wName, 7.5, doc, 'bold'),
                wrapTextWithGermanHyphenation((u.ablageort || '').trim(), wOrt, 7.5, doc, 'normal'),
                wrapTextWithGermanHyphenation((u.bemerkung || '').trim(), wBem, 7.5, doc, 'normal'),
            ]),
            // fillColor: false — sonst decken die Zeilen das Wasserzeichen zu.
            styles: {
                fontSize: 7.5,
                cellPadding: 2,
                lineColor: COLORS.veryLight,
                lineWidth: 0.1,
                textColor: COLORS.text,
                overflow: 'linebreak',
                valign: 'top',
                fillColor: false
            },
            rowPageBreak: 'avoid',
            headStyles: { fillColor: COLORS.black, textColor: COLORS.white, fontStyle: 'bold', fontSize: 6.5, cellPadding: 2 },
            alternateRowStyles: { fillColor: COLORS.bg },
            columnStyles: {
                0: { cellWidth: wName, fontStyle: 'bold', textColor: COLORS.black },
                1: { cellWidth: wOrt },
                2: { cellWidth: wBem }
            },
            // Wie Adressverzeichnis und Katalog: nur VOR dem Inhalt zeichnen,
            // Seite 1 der Tabelle hat Bar und Wasserzeichen schon.
            willDrawPage: (data: { pageNumber: number }) => {
                if (data.pageNumber > 1) {
                    drawThemeBar();
                    drawWatermark();
                    drawContinuationHeader('Mitgeltende Unterlagen (Fortsetzung)', CONT_HEAD_Y);
                }
            }
        });
    };

    // ════════════════════════════════════════════
    // APPENDIX PAGES (Anhang / Dokumentation)
    // ════════════════════════════════════════════
    const renderAppendix = async () => {
        const images = projectData.appendixImages || [];
        if (images.length === 0) return;

        addPage();
        drawThemeBar();
        drawWatermark();

        let y = drawSectionHeader('Anhang / Dokumentation', 'Bild- & Risikoanalyse', MG_T + 10);
        const maxY = A4_H - MG_B;

        for (let imgIdx = 0; imgIdx < images.length; imgIdx++) {
            const img = images[imgIdx];
            const findingsCount = (img.findings || []).length;
            const neededHeight = 80 + Math.max(findingsCount, 1) * 6 + 15;

            if (y + neededHeight > maxY) {
                addPage();
                drawThemeBar(); drawWatermark();
                y = drawContinuationHeader('Anhang (Fortsetzung)', MG_T + 10);
            }

            // Image
            let imgW = 150;
            let imgH = 65;
            
            if (img.size === 'small') {
                imgW = 100;
                imgH = 43;
            } else if (img.size === 'large') {
                imgW = CONTENT_W;
                imgH = 85;
            }
            
            const imgX = MG_L + (CONTENT_W - imgW) / 2;

            try {
                doc.setDrawColor(...COLORS.veryLight);
                doc.rect(imgX, y, imgW, imgH);
                
                // Get image dimensions to maintain aspect ratio without stretching
                const imgProps = doc.getImageProperties(img.imageData);
                const imgRatio = imgProps.width / imgProps.height;
                const boxRatio = imgW / imgH;
                
                let drawW = imgW;
                let drawH = imgH;
                let drawX = imgX;
                let drawY = y;
                
                if (imgRatio > boxRatio) {
                    // Image is wider than box
                    drawH = imgW / imgRatio;
                    drawY = y + (imgH - drawH) / 2;
                } else {
                    // Image is taller than box
                    drawW = imgH * imgRatio;
                    drawX = imgX + (imgW - drawW) / 2;
                }
                
                doc.addImage(img.imageData, 'AUTO', drawX + 0.5, drawY + 0.5, drawW - 1, drawH - 1);

                // Draw finding markers on image
                if (img.findings) {
                    img.findings.forEach((finding, fIdx) => {
                        const cx = drawX + ((finding.box.xmin + finding.box.xmax) / 2 / 100) * drawW;
                        const cy = drawY + ((finding.box.ymin + finding.box.ymax) / 2 / 100) * drawH;
                        doc.setFillColor(220, 38, 38); // red
                        doc.circle(cx, cy, 3, 'F');
                        doc.setFillColor(255, 255, 255);
                        doc.circle(cx, cy, 2.5, 'F');
                        doc.setFillColor(220, 38, 38);
                        doc.circle(cx, cy, 2, 'F');
                        doc.setFontSize(6);
                        doc.setFont('helvetica', 'bold');
                        doc.setTextColor(255, 255, 255);
                        doc.text(`${fIdx + 1}`, cx, cy + 0.8, { align: 'center' });
                    });
                }
            } catch (e) {
                console.warn('Appendix image error:', e);
                doc.setFillColor(...COLORS.bg);
                doc.rect(imgX, y, imgW, imgH, 'F');
                doc.setFontSize(8);
                doc.setTextColor(...COLORS.light);
                doc.text('Bild konnte nicht geladen werden', A4_W / 2, y + imgH / 2, { align: 'center' });
            }

            y += imgH + 5;

            // Findings list
            doc.setFontSize(6);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...COLORS.muted);
            doc.text('GEFÄHRDUNGSBEURTEILUNG', MG_L, y);
            y += 1;
            doc.setDrawColor(...COLORS.veryLight);
            doc.line(MG_L, y, A4_W - MG_R, y);
            y += 3;

            if (img.findings && img.findings.length > 0) {
                img.findings.forEach((finding, fIdx) => {
                    const titleVal = finding.title || 'Auffälligkeit';
                    const descVal = finding.description || '';
                    
                    // Pre-calculate heights & wrap lines
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'bold');
                    const titleLines = wrapText(titleVal, CONTENT_W - 10, 8);
                    
                    doc.setFontSize(7.5);
                    doc.setFont('helvetica', 'normal');
                    const descLines = descVal ? wrapText(descVal, CONTENT_W - 10, 7.5) : [];
                    
                    const blockHeight = titleLines.length * 4 + (descLines.length > 0 ? descLines.length * 3.5 + 2 : 0) + 4;
                    
                    if (y + blockHeight > maxY) {
                        addPage();
                        drawThemeBar(); drawWatermark();
                        y = drawContinuationHeader('Anhang (Fortsetzung)', MG_T + 10);
                    }

                    // Number circle
                    doc.setFillColor(220, 38, 38);
                    doc.circle(MG_L + 3, y - 0.8, 1.8, 'F');
                    doc.setFontSize(5.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(255, 255, 255);
                    doc.text(`${fIdx + 1}`, MG_L + 3, y - 0.1, { align: 'center' });

                    // Title
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(185, 28, 28); // red-700
                    doc.text(titleLines, MG_L + 8, y);
                    y += titleLines.length * 4;

                    // Description
                    if (descLines.length > 0) {
                        y += 1;
                        doc.setFontSize(7.5);
                        doc.setFont('helvetica', 'normal');
                        doc.setTextColor(...COLORS.text);
                        doc.text(descLines, MG_L + 8, y);
                        y += descLines.length * 3.5;
                    }
                    
                    y += 4; // spacing between findings
                });
            } else {
                doc.setFontSize(7);
                doc.setFont('helvetica', 'italic');
                doc.setTextColor(...COLORS.light);
                doc.text('Keine Auffälligkeiten dokumentiert.', MG_L, y);
                y += 5;
            }

            y += 10; // spacing between images
        }
    };

    // ════════════════════════════════════════════
    // EXECUTE GENERATION
    // ════════════════════════════════════════════
    try {
        await renderCover();
        renderAddressPage();
        renderPreamble();
        renderCatalog();
        renderMitgeltendeUnterlagen();
        await renderAppendix();

        // ── ADD PAGE NUMBERS ──
        const totalPageCount = doc.getNumberOfPages();
        for (let i = 1; i <= totalPageCount; i++) {
            doc.setPage(i);
            if (i > 1) drawFooter(i, totalPageCount); // No footer on cover
        }

        // ── SAVE ──
        // Der Name kommt aus ufsaAblage, damit Download und Projektablage
        // dieselbe Datei gleich nennen.
        if (output === 'save') doc.save(unterlagePdfDateiname(projectData.projectName));
        return doc;
    } catch (error) {
        console.error('PDF Generation Error:', error);
        throw error;
    }
};